import { useCallback, useEffect, useState } from 'react';
import { addTrackFromLink, linkForSavedVideo } from '../../../../services/TrackJobsService';
import { linkTrackState, removeVocalsForVideo } from '../../../../services/trackJobsModel';

/**
 * "Make a track" from a song's saved video (Anthony, 2026-09-27): the video
 * was already looked up for the lesson, so one tap by it sends its link to
 * the same queue as Add a track's "Paste a link", with no second search and
 * no copy-paste. The Karaoke video goes in as the Track; a Lyrics or
 * Performance video has its vocals removed (removeVocalsForVideo).
 *
 * The button's state comes from the job queue itself (linkTrackState), so a
 * reload or a second teacher sees the same thing. `starting` only covers the
 * moment between the tap and the job's first snapshot, which is what stops a
 * double tap from queueing the same track twice.
 *
 * Returns null when there is nothing to make a track FOR (no student, no
 * song tab, no signed-in teacher), so the dock renders no button at all.
 */
const useVideoTrack = ({ studentId, studentName = null, song = null, uid = null, jobs = [] }) => {
	const [starting, setStarting] = useState(() => new Set());
	const [error, setError] = useState('');

	useEffect(() => {
		setStarting(new Set());
		setError('');
	}, [song?.id, studentId]);

	const videos = song?.videos || {};

	const stateFor = useCallback(
		(kind) => {
			const url = linkForSavedVideo(videos[kind]);
			if (!url) return 'unavailable';
			const s = linkTrackState(jobs, song, url);
			if (s === 'idle' && starting.has(kind)) return 'working';
			return s;
		},
		[videos, jobs, song, starting]
	);

	const make = useCallback(
		async (kind) => {
			const url = linkForSavedVideo(videos[kind]);
			const now = url ? linkTrackState(jobs, song, url) : 'unavailable';
			// Guarded here, not only by the button's disabled state.
			// `starting` blocks only while no job is visible yet: once the queue
			// shows one, its own state rules (so a failed one can be retried).
			if (!url || !studentId || !song || !uid || (starting.has(kind) && now === 'idle') || now === 'working' || now === 'done') return;
			setError('');
			setStarting((prev) => new Set([...prev, kind]));
			try {
				await addTrackFromLink({ url, studentId, studentName, song, removeVocals: removeVocalsForVideo(kind), uid });
			} catch (e) {
				console.error('Make a track from video failed:', e);
				setError((e && e.message) || 'unknown error');
				setStarting((prev) => {
					const next = new Set(prev);
					next.delete(kind);
					return next;
				});
			}
		},
		[videos, jobs, song, studentId, studentName, uid, starting]
	);

	// Pass 3 A2: the teacher can put the error away; the next make clears it
	// too. Above the early return: a hook after it breaks the rules of hooks.
	const clearError = useCallback(() => setError(''), []);

	if (!studentId || !song || !uid) return null;
	return { stateFor, make, error, clearError };
};

export default useVideoTrack;
